import { PanelPosition } from "./types";
import { PART_TYPE_IDS } from "../../models";
import { PANEL_COLORS_BY_POSITION, RoofPanelColors } from "../../theme/colors";
import { Viewer3DSettings } from "../../../../shared/theme";

// Fixed colors for each panel position (indexed by positionId, not partTypeId)
// This allows unified part types to have different colors in different positions
export const PANEL_COLORS: Record<number, string> = PANEL_COLORS_BY_POSITION;

// Spider frame color (blue structural frame)
export const SPIDER_FRAME_COLOR = RoofPanelColors.SPIDER;

// Empty position color (gray)
export const EMPTY_POSITION_COLOR = RoofPanelColors.EMPTY_POSITION;

// Selection highlight color
export const SELECTION_COLOR = RoofPanelColors.SELECTION;

// Hover/Selection highlight intensity (from shared 3D viewer settings)
export const HOVER_EMISSIVE_INTENSITY =
  Viewer3DSettings.HOVER_EMISSIVE_INTENSITY;
export const SELECTION_EMISSIVE_INTENSITY =
  Viewer3DSettings.SELECTION_EMISSIVE_INTENSITY;

// Geometry dimensions - adjusted to match reference image proportions
export const ROOF_DIMENSIONS = {
  // Radii for different rings (from center outward)
  // Delta Ring is composed of TWO torus rings (not a solid center)
  // The two rings should be close together with no gap
  deltaRingInner: 0.42, // Inner torus ring radius (close to outer) - increased
  deltaRingOuter: 0.48, // Outer torus ring radius - increased
  // Inner ring starts right after delta (panels start here)
  innerRingInner: 0.52, // Increased to match larger delta ring
  // Panels end here - leave room for skirt band at outer edge
  panelOuterRadius: 0.95,
  // Outer edge of roof - skirt band extends to here
  outerRingOuter: 1.15,
  // Heights and dome curvature
  panelHeight: 0.06,
  frameHeight: 0.1,
  frameWidth: 0.035,
  // Dome curve - height offset from outer to inner edge
  domeHeight: 0.12,
  // Skirt drop at outer edge (panels curve down)
  skirtDrop: 0.06,
};

// Panel positions configuration
// Based on the physical layout of the EAF roof from reference image
// Angles are in radians, 0 = right (positive X), counter-clockwise
// The segments are NOT equal - they follow the actual roof layout
// Looking at the reference: 0° = right side (purple/pink), going counter-clockwise

// Helper to convert degrees to radians
const deg2rad = (deg: number) => (deg * Math.PI) / 180;

// Segment boundaries based on the reference image
// Three.js coordinate system: 0° = right (positive X), counter-clockwise positive
// So: 0° = 3 o'clock, 90° = 12 o'clock, 180° = 9 o'clock, 270° = 6 o'clock
//
// STRUCTURE:
// - 6 MAIN PANELS (inner ring, from delta to panelOuterRadius)
// - 2 SKIRTS (outer band, from panelOuterRadius to outerRingOuter)
// - 8 SPIDER LEGS at panel boundaries
//
// Layout: 6 panels of ~equal size (~55° each), with 4th Hole Panel slightly larger (~70°)
// 4th Hole Panel at TOP (90°), Center 60 Degree OPPOSITE at BOTTOM (270°)
//
// Going counter-clockwise from top (90° = 12 o'clock):
// 1. 4th Hole Panel/Skirt (Purple) - top, centered at 90°, ~70° span (55° to 125°)
// 2. South 55 Degree (Cyan) - top-left, ~55° span (125° to 180°)
// 3. South 60 Degree (Orange) - left, ~55° span (180° to 235°)
// 4. Center 60 Degree (Red) - bottom, centered at 270°, ~55° span (235° to 290°)
// 5. North 60 Degree (Orange) - right, ~55° span (290° to 345°)
// 6. North 55 Degree (Cyan) - top-right, ~55° span (345° to 55°, wrapping through 0°)
//
// SKIRTS (outer band):
// - South Skirt (Brown): from South 55° start (125°) to middle of Center 60° (270°)
// - North Skirt (Cyan): from middle of Center 60° (270°) to North 55° end (55°, wrapping)
//
// 8 SPIDER LEGS at: all 6 panel boundaries + middle of Center 60 + middle of North 60
// Panel size calculation:
// - 4th Hole Panel: 70° (larger, at top)
// - Remaining 5 panels: 290° / 5 = 58° each
//
// Starting from 4th Hole Panel centered at 90°:
// - 4th Hole Panel: 55° to 125° (70°)
// - South 55: 125° to 183° (58°)
// - South 60: 183° to 241° (58°)
// - Center 60: 241° to 299° (58°) - OPPOSITE to 4th Hole, centered at 270°
// - North 60: 299° to 357° (58°)
// - North 55: 357° to 55° (58°, wraps through 0°)
const SEGMENT_ANGLES = {
  // 4th Hole Panel/Skirt (Purple) - top, larger segment (70°)
  fourthHolePanelStart: deg2rad(55), // Start after North 55
  fourthHolePanelEnd: deg2rad(125), // End before South 55
  // 4th Hole Elbow (Yellow) - the hole opening CENTERED in the purple panel (at 90°)
  fourthHoleElbowStart: deg2rad(75),
  fourthHoleElbowEnd: deg2rad(105),
  // South 55 Degree (Cyan) - top-left (58°)
  south55Start: deg2rad(125),
  south55End: deg2rad(183),
  // South 60 Degree (Orange) - left (58°)
  south60Start: deg2rad(183),
  south60End: deg2rad(241),
  // Center 60 Degree (Red) - bottom, OPPOSITE to 4th Hole (58°)
  center60Start: deg2rad(241),
  center60End: deg2rad(299),
  center60Middle: deg2rad(270), // Middle spoke for Center 60 (exactly at bottom)
  // North 60 Degree (Orange) - right (58°)
  north60Start: deg2rad(299),
  north60End: deg2rad(357),
  north60Middle: deg2rad(328), // Middle spoke for North 60
  // North 55 Degree (Cyan) - top-right (58°, wraps through 0°)
  north55Start: deg2rad(357),
  north55End: deg2rad(55), // Wraps to start of 4th Hole Panel
  // South Skirt (Green outer band) - from South 55 start to middle of Center 60
  southSkirtStart: deg2rad(125), // Same as South 55 start
  southSkirtEnd: deg2rad(270), // Ends at middle of Center 60 (where spider leg divides)
  // North Skirt (Cyan outer band) - from middle of Center 60 to North 55 end
  northSkirtStart: deg2rad(270), // Starts at middle of Center 60 (where spider leg divides)
  northSkirtEnd: deg2rad(55), // Wraps around to 4th Hole Panel start
};

// 6 Main Panels (inner ring) + Delta Ring + 4th Hole Elbow
// Panels go from innerRingInner to panelOuterRadius (NOT to outer edge)
// Skirts are separate pieces at the outer band
export const PANEL_POSITIONS: PanelPosition[] = [
  // Delta Ring - Center (rendered as ring around electrode holes)
  {
    id: PART_TYPE_IDS.DELTA_RING,
    name: "Delta Ring",
    positionId: 7,
    startAngle: 0,
    endAngle: Math.PI * 2,
    innerRadius: ROOF_DIMENSIONS.deltaRingInner,
    outerRadius: ROOF_DIMENSIONS.deltaRingOuter,
    color: PANEL_COLORS[7], // Position 7: Delta Ring
    height: ROOF_DIMENSIONS.panelHeight,
    ring: "center",
  },

  // Panel 1: 4th Hole Panel/Skirt (Purple) - top, includes yellow elbow hole
  // This panel DOES extend to outer edge (has integrated skirt)
  {
    id: PART_TYPE_IDS.FOURTH_HOLE_PANEL_SKIRT,
    name: "4th Hole Panel/Skirt",
    positionId: 2,
    startAngle: SEGMENT_ANGLES.fourthHolePanelStart, // 55°
    endAngle: SEGMENT_ANGLES.fourthHolePanelEnd, // 125°
    innerRadius: ROOF_DIMENSIONS.innerRingInner,
    outerRadius: ROOF_DIMENSIONS.outerRingOuter, // Extends to outer edge
    color: PANEL_COLORS[2], // Position 2: 4th Hole Panel/Skirt
    height: ROOF_DIMENSIONS.panelHeight,
    ring: "inner",
  },

  // 4th Hole Elbow (Yellow) - the hole opening WITHIN the purple panel (oval shape centered at 90°)
  {
    id: PART_TYPE_IDS.FOURTH_HOLE_ELBOW,
    name: "4th Hole Elbow",
    positionId: 3,
    startAngle: SEGMENT_ANGLES.fourthHoleElbowStart, // 75° (unused for oval)
    endAngle: SEGMENT_ANGLES.fourthHoleElbowEnd, // 105° (unused for oval)
    innerRadius: ROOF_DIMENSIONS.innerRingInner * 0.7, // (unused for oval)
    outerRadius: ROOF_DIMENSIONS.innerRingInner * 1.3, // (unused for oval)
    color: PANEL_COLORS[3], // Position 3: 4th Hole Elbow
    height: ROOF_DIMENSIONS.panelHeight * 1.5,
    ring: "inner",
    // Oval shape configuration
    shape: "oval",
    ovalRadiusX: 0.35, // Horizontal radius (wider)
    ovalRadiusY: 0.24, // Vertical radius (narrower)
    ovalCenterAngle: deg2rad(90), // Centered at top (90°)
    ovalCenterRadius:
      (ROOF_DIMENSIONS.innerRingInner + ROOF_DIMENSIONS.panelOuterRadius) / 2 +
      0.05, // Middle of panel ring + offset from spider
  },

  // Panel 2: South 55 Degree (Orange) - top-left (58°)
  // Uses unified 55° panel type - can accept any 55° panel
  {
    id: PART_TYPE_IDS.FIFTY_FIVE_DEGREE, // Unified 55° panel type
    name: "South 55 Degree",
    positionId: 4,
    startAngle: SEGMENT_ANGLES.south55Start, // 125°
    endAngle: SEGMENT_ANGLES.south55End, // 183°
    innerRadius: ROOF_DIMENSIONS.innerRingInner,
    outerRadius: ROOF_DIMENSIONS.panelOuterRadius, // Inner panels only
    color: PANEL_COLORS[4], // Position 4: South 55 (Orange)
    height: ROOF_DIMENSIONS.panelHeight,
    ring: "inner",
  },

  // Panel 3: South 60 Degree (Red) - left (58°)
  // Uses unified 60° panel type - can accept any 60° panel
  {
    id: PART_TYPE_IDS.SIXTY_DEGREE, // Unified 60° panel type
    name: "South 60 Degree",
    positionId: 5,
    startAngle: SEGMENT_ANGLES.south60Start, // 183°
    endAngle: SEGMENT_ANGLES.south60End, // 241°
    innerRadius: ROOF_DIMENSIONS.innerRingInner,
    outerRadius: ROOF_DIMENSIONS.panelOuterRadius, // Inner panels only
    color: PANEL_COLORS[5], // Position 5: South 60 (Red)
    height: ROOF_DIMENSIONS.panelHeight,
    ring: "inner",
  },

  // Panel 4: Center 60 Degree (Green) - bottom, OPPOSITE to 4th Hole (58°)
  {
    id: PART_TYPE_IDS.CENTER_60_DEGREE,
    name: "Center 60 Degree",
    positionId: 8,
    startAngle: SEGMENT_ANGLES.center60Start, // 241°
    endAngle: SEGMENT_ANGLES.center60End, // 299°
    innerRadius: ROOF_DIMENSIONS.innerRingInner,
    outerRadius: ROOF_DIMENSIONS.panelOuterRadius, // Inner panels only
    color: PANEL_COLORS[8], // Position 8: Center 60 (Green)
    height: ROOF_DIMENSIONS.panelHeight,
    ring: "inner",
  },

  // Panel 5: North 60 Degree (Bordeaux) - right (58°)
  // Uses unified 60° panel type - can accept any 60° panel
  {
    id: PART_TYPE_IDS.SIXTY_DEGREE, // Unified 60° panel type
    name: "North 60 Degree",
    positionId: 9,
    startAngle: SEGMENT_ANGLES.north60Start, // 299°
    endAngle: SEGMENT_ANGLES.north60End, // 357°
    innerRadius: ROOF_DIMENSIONS.innerRingInner,
    outerRadius: ROOF_DIMENSIONS.panelOuterRadius, // Inner panels only
    color: PANEL_COLORS[9], // Position 9: North 60 (Bordeaux)
    height: ROOF_DIMENSIONS.panelHeight,
    ring: "inner",
  },

  // Panel 6: North 55 Degree (Yellow) - top-right (58°, wraps through 0°)
  // Uses unified 55° panel type - can accept any 55° panel
  {
    id: PART_TYPE_IDS.FIFTY_FIVE_DEGREE, // Unified 55° panel type
    name: "North 55 Degree",
    positionId: 11,
    startAngle: SEGMENT_ANGLES.north55Start, // 357°
    endAngle: SEGMENT_ANGLES.north55End, // 55° (wraps through 0°)
    innerRadius: ROOF_DIMENSIONS.innerRingInner,
    outerRadius: ROOF_DIMENSIONS.panelOuterRadius, // Inner panels only
    color: PANEL_COLORS[11], // Position 11: North 55 (Yellow)
    height: ROOF_DIMENSIONS.panelHeight,
    ring: "inner",
  },
];

// Lateral skirt pieces - outer band from panelOuterRadius to outerRingOuter
// South Skirt: from South 55 start (125°) to Center 60 start (241°)
// North Skirt: from Center 60 end (299°) to North 55 end (55°, wrapping through 0°)
export const SKIRT_PIECES: PanelPosition[] = [
  // South Skirt (Bronze) - outer band from 125° to 241°
  // Uses unified Skirt panel type - can accept any Skirt panel
  {
    id: PART_TYPE_IDS.SKIRT, // Unified Skirt panel type
    name: "South Skirt",
    positionId: 6,
    startAngle: SEGMENT_ANGLES.southSkirtStart, // 125°
    endAngle: SEGMENT_ANGLES.southSkirtEnd, // 241°
    innerRadius: ROOF_DIMENSIONS.panelOuterRadius,
    outerRadius: ROOF_DIMENSIONS.outerRingOuter,
    color: PANEL_COLORS[6], // Position 6: South Skirt (Bronze)
    height: ROOF_DIMENSIONS.panelHeight,
    ring: "outer",
  },
  // North Skirt (Cyan) - outer band from 299° to 55° (wrapping through 0°)
  // Uses unified Skirt panel type - can accept any Skirt panel
  {
    id: PART_TYPE_IDS.SKIRT, // Unified Skirt panel type
    name: "North Skirt",
    positionId: 10,
    startAngle: SEGMENT_ANGLES.northSkirtStart, // 299°
    endAngle: SEGMENT_ANGLES.northSkirtEnd, // 55° (wraps through 360°)
    innerRadius: ROOF_DIMENSIONS.panelOuterRadius,
    outerRadius: ROOF_DIMENSIONS.outerRingOuter,
    color: PANEL_COLORS[10], // Position 10: North Skirt (Cyan)
    height: ROOF_DIMENSIONS.panelHeight,
    ring: "outer",
  },
];

// Spoke angles for the spider frame (based on segment boundaries)
// 8 spokes total:
// 1. Between North 55 and 4th Hole Panel (55°)
// 2. Between 4th Hole Panel and South 55 (125°)
// 3. Between South 55 and South 60 (183°)
// 4. Between South 60 and Center 60 (241°)
// 5. Middle of Center 60 (270°) - divides the panel (exactly at bottom)
// 6. Between Center 60 and North 60 (299°)
// 7. Middle of North 60 (328°) - divides the panel
// 8. Between North 60 and North 55 (357°)
export const SPIDER_SPOKE_ANGLES = [
  SEGMENT_ANGLES.fourthHolePanelStart, // 55° - Between North 55 and 4th Hole Panel
  SEGMENT_ANGLES.fourthHolePanelEnd, // 125° - Between 4th Hole Panel and South 55
  SEGMENT_ANGLES.south55End, // 183° - Between South 55 and South 60
  SEGMENT_ANGLES.south60End, // 241° - Between South 60 and Center 60
  SEGMENT_ANGLES.center60Middle, // 270° - Middle of Center 60 (panel division)
  SEGMENT_ANGLES.center60End, // 299° - Between Center 60 and North 60
  SEGMENT_ANGLES.north60Middle, // 328° - Middle of North 60 (panel division)
  SEGMENT_ANGLES.north60End, // 357° - Between North 60 and North 55
];

// Spider frame is a special case - it's the structural frame, not a panel
// Position 1 is for Spider (the blue structural frame)
export const SPIDER_POSITION: PanelPosition = {
  id: PART_TYPE_IDS.SPIDER,
  name: "Spider",
  positionId: 1,
  startAngle: 0,
  endAngle: Math.PI * 2,
  innerRadius: ROOF_DIMENSIONS.deltaRingOuter,
  outerRadius: ROOF_DIMENSIONS.innerRingInner,
  color: SPIDER_FRAME_COLOR,
  height: ROOF_DIMENSIONS.frameHeight,
};

// Get panel position by PartTypeId
export const getPanelPositionById = (
  partTypeId: number,
): PanelPosition | undefined => {
  if (partTypeId === PART_TYPE_IDS.SPIDER) {
    return SPIDER_POSITION;
  }
  return PANEL_POSITIONS.find((p) => p.id === partTypeId);
};

// Get panel position by positionId (1-11)
export const getPanelPositionByPositionId = (
  positionId: number,
): PanelPosition | undefined => {
  if (positionId === 1) {
    return SPIDER_POSITION;
  }
  // Check main panels first
  const panel = PANEL_POSITIONS.find((p) => p.positionId === positionId);
  if (panel) return panel;
  // Check skirt pieces
  return SKIRT_PIECES.find((p) => p.positionId === positionId);
};

// Get color for a panel by position ID, with fallback for empty positions
export const getPanelColor = (
  positionId: number | undefined,
  hasAssignedPart: boolean,
): string => {
  if (!hasAssignedPart) {
    return EMPTY_POSITION_COLOR;
  }
  return PANEL_COLORS[positionId ?? 0] ?? EMPTY_POSITION_COLOR;
};

// Camera settings - adjusted for GLB model (~1.5 units wide after 0.2 scale)
export const CAMERA_SETTINGS = {
  position: [0, 1.5, 1.5] as [number, number, number],
  fov: 50,
  near: 0.1,
  far: 100,
};

// OrbitControls settings - adjusted for GLB model
export const ORBIT_CONTROLS_SETTINGS = {
  enablePan: true,
  enableZoom: true,
  enableRotate: true,
  minDistance: 1,
  maxDistance: 10,
  minPolarAngle: 0,
  maxPolarAngle: Math.PI / 2.2, // ~81 degrees - prevent going under
  enableDamping: true,
  dampingFactor: 0.05,
};

// Lighting settings
export const LIGHTING_SETTINGS = {
  ambientIntensity: 0.6,
  directionalIntensity: 0.8,
  directionalPosition: [5, 10, 5] as [number, number, number],
};

// =============================================================================
// SVG THUMBNAIL CONSTANTS
// Geometry and layout constants for the miniature SVG representation
// =============================================================================

/**
 * SVG thumbnail layout settings
 * Controls scale and stroke widths within the thumbnail
 */
export const SVG_THUMBNAIL_LAYOUT = {
  // Scale factor to fit model within thumbnail (with padding)
  SCALE_FACTOR: 0.85,
  // Stroke width for panel/skirt borders
  STROKE_WIDTH: 0.5,
  // Ring thickness for delta ring strokes
  RING_THICKNESS: 2,
  // Outer frame ring stroke width
  OUTER_FRAME_STROKE: 2,
  // Inner frame ring stroke width
  INNER_FRAME_STROKE: 1.5,
  // Spider spoke stroke width
  SPOKE_STROKE: 1.5,
} as const;

/**
 * SVG thumbnail electrode holes configuration
 * Settings for the 3 electrode holes in the Delta Ring area
 */
export const SVG_THUMBNAIL_ELECTRODES = {
  // Radius of each electrode hole (multiplied by scale)
  RADIUS_FACTOR: 0.1,
  // Distance from center to electrode holes (multiplied by scale)
  DISTANCE_FACTOR: 0.16,
  // Angle offset for top holes from vertical (in degrees)
  // Creates equidistant triangle arrangement
  TOP_HOLE_ANGLE_OFFSET: 60, // ~60° offset for equidistant triangle
} as const;
